import {
  containersSelector,
  metersSelector,
  SiteType,
  useGetContainersBySiteQuery,
  useGetContainersByWellQuery,
  useGetMetersByObjectQuery
} from "@ots-frontend/ots-data";
import {INode} from "react-digraph";
import {Card, CardProps} from "../Card/Card";
import {Group, Loader, Text} from "@mantine/core";
import {useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {useDisclosure} from "@mantine/hooks";
import {useTranslation} from "react-i18next";
import {MeterDataTableModal} from "../../data-table/MeterDataTable/MeterDataTable";
import {ContainerDataTableModal} from "../../data-table/ContainerDataTable/ContainerDataTable";

export type PipelineGraphNodeCardProps = CardProps & {
  node: INode
}

export const PipelineGraphNodeCard = (props: PipelineGraphNodeCardProps) => {
  const {
    node,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const {t} = useTranslation(["common"])

  const {regionId} = useApp()

  const meterModalDisclosure = useDisclosure(false);
  const meterModalOpened = meterModalDisclosure[0]
  const meterModalHandlers = meterModalDisclosure[1]

  const containerModalDisclosure = useDisclosure(false);
  const containerModalOpened = containerModalDisclosure[0]
  const containerModalHandlers = containerModalDisclosure[1]

  const selectContainers = useMemo(() => {
    return containersSelector();
  }, []);

  const siteContainersArg = useMemo(() => {
    if (regionId && node.data.objectType === 'SITE') {
      return {
        regionId: regionId,
        siteId: node.data.objectId,
      }
    } else {
      return skipToken
    }
  }, [node, regionId])

  const {siteContainers, isSiteContainersLoading} = useGetContainersBySiteQuery(siteContainersArg, {
    selectFromResult: (result) => ({
      siteContainers: selectContainers(result, {}),
      isSiteContainersLoading: result.isLoading
    })
  });

  const wellContainersArg = useMemo(() => {
    if (regionId && node.data.objectType === 'WELL') {
      return {
        regionId: regionId,
        wellId: node.data.objectId,
      }
    } else {
      return skipToken
    }
  }, [node, regionId])

  const {wellContainers, isWellContainersLoading} = useGetContainersByWellQuery(wellContainersArg, {
    selectFromResult: (result) => ({
      wellContainers: selectContainers(result, {}),
      isWellContainersLoading: result.isLoading
    })
  });

  const containers = useMemo(() => {
    if (node.data.objectType === 'SITE') {
      return siteContainers
    } else if (node.data.objectType === 'WELL') {
      return wellContainers
    } else {
      return undefined
    }
  }, [node, siteContainers, wellContainers])

  const isContainersLoading = useMemo(() => {
    return isSiteContainersLoading || isWellContainersLoading
  }, [isSiteContainersLoading, isWellContainersLoading])

  const selectMeters = useMemo(() => {
    return metersSelector();
  }, []);

  const {meters, isMetersLoading} = useGetMetersByObjectQuery(regionId ? {
    id: node.data.objectId as string,
    type: node.data.objectType as string,
    regionId: regionId,
  } : skipToken, {
    selectFromResult: (result) => ({
      meters: selectMeters(result, {}),
      isMetersLoading: result.isLoading
    })
  });

  return (
    <Card mah={300} w={350} shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>
      <Group position="apart" mt="md">
        <div>
          <Text fw={500}>{node.title}</Text>
          <Text fz="xs" c="dimmed">
            {node.data?.objectType}
          </Text>
          {node.data?.siteType &&
            <Text fz="xs" c="dimmed">
              {
                t((node.data.siteType as SiteType).displayValue.id, (node.data.siteType as SiteType).displayValue.defaultMessage)
              }
            </Text>
          }
        </div>
      </Group>

      <Card.Section mt="md" p="md">
        {(isContainersLoading || isMetersLoading) ? (
          <>
            <Loader size="xs" color="gray"/>
            <Text size="sm" c="dimmed">
              {t("common.state.loading", "Loading")}
            </Text>
          </>
        ) : (
          <>
            {meters &&
              <Text fz="sm" c="dimmed" td="underline" style={{cursor: "pointer"}}
                    onClick={() => meterModalHandlers.open()}>
                {`${meters.length} Meters`}
              </Text>
            }
            {(containers && node.data.objectType !== 'CONTAINER') &&
              <Text fz="sm" c="dimmed" td="underline" style={{cursor: "pointer"}}
                    onClick={() => containerModalHandlers.open()}>
                {`${containers.length} Containers`}
              </Text>
            }
          </>
        )}
      </Card.Section>

      <MeterDataTableModal
        size="xl"
        opened={meterModalOpened}
        onClose={meterModalHandlers.close}
        dataTable={{
          withBorder: true,
          records: meters ?? []
        }}/>

      <ContainerDataTableModal
        size="xl"
        opened={containerModalOpened}
        onClose={containerModalHandlers.close}
        dataTable={{
          withBorder: true,
          records: containers ?? []
        }}/>
    </Card>
  )
}
